import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1StoresStoreIdDiscountsId,
  deleteAdminV1StoresStoreIdDiscountsIdCodesCodeId,
  getAdminV1StoresStoreIdDiscounts,
  getAdminV1StoresStoreIdDiscountsId,
  getAdminV1StoresStoreIdDiscountsIdCodes,
  getAdminV1StoresStoreIdDiscountsIdUsages,
  patchAdminV1StoresStoreIdDiscountsId,
  patchAdminV1StoresStoreIdDiscountsIdCodesCodeId,
  postAdminV1StoresStoreIdDiscounts,
  postAdminV1StoresStoreIdDiscountsIdCodes,
  postAdminV1StoresStoreIdExports,
  type Discount,
  type DiscountCode,
  type ExportJob,
  type GetAdminV1StoresStoreIdDiscounts200,
  type GetAdminV1StoresStoreIdDiscountsIdCodes200,
  type GetAdminV1StoresStoreIdDiscountsIdCodesParams,
  type GetAdminV1StoresStoreIdDiscountsIdUsages200,
  type GetAdminV1StoresStoreIdDiscountsIdUsagesParams,
  type PatchAdminV1StoresStoreIdDiscountsIdBody,
  type PostAdminV1StoresStoreIdDiscountsBody,
  type PatchAdminV1StoresStoreIdDiscountsIdCodesCodeIdBody,
  type PostAdminV1StoresStoreIdDiscountsIdCodesBody,
} from '@nucleus-commerce/api-client/admin';

/**
 * List params as `listParams` builds them from the route's search: `q`, `sort`, the flat filter
 * params (`status=active`, `startsAt_gte=2026-09-01`), `cursor`, `limit`.
 */
export type DiscountListParams = ListParams;

/** Code and automatic discounts of one store, their codes and the orders that used them. */
export const discountsApi = {
  list: (
    storeId: string,
    params: DiscountListParams,
  ): Promise<GetAdminV1StoresStoreIdDiscounts200> =>
    getAdminV1StoresStoreIdDiscounts({ storeId }, params),
  get: (storeId: string, id: string): Promise<Discount> =>
    getAdminV1StoresStoreIdDiscountsId({ storeId, id }),
  create: (storeId: string, body: PostAdminV1StoresStoreIdDiscountsBody): Promise<Discount> =>
    postAdminV1StoresStoreIdDiscounts({ storeId }, body),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdDiscountsIdBody,
  ): Promise<Discount> => patchAdminV1StoresStoreIdDiscountsId({ storeId, id }, body),
  remove: (storeId: string, id: string): Promise<void> =>
    deleteAdminV1StoresStoreIdDiscountsId({ storeId, id }),
  addCodes: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdDiscountsIdCodesBody,
  ): Promise<Discount> => postAdminV1StoresStoreIdDiscountsIdCodes({ storeId, id }, body),
  codes: (
    storeId: string,
    id: string,
    params: GetAdminV1StoresStoreIdDiscountsIdCodesParams,
  ): Promise<GetAdminV1StoresStoreIdDiscountsIdCodes200> =>
    getAdminV1StoresStoreIdDiscountsIdCodes({ storeId, id }, params),
  updateCode: (
    storeId: string,
    id: string,
    codeId: string,
    body: PatchAdminV1StoresStoreIdDiscountsIdCodesCodeIdBody,
  ): Promise<DiscountCode> =>
    patchAdminV1StoresStoreIdDiscountsIdCodesCodeId({ storeId, id, codeId }, body),
  removeCode: (storeId: string, id: string, codeId: string): Promise<void> =>
    deleteAdminV1StoresStoreIdDiscountsIdCodesCodeId({ storeId, id, codeId }),
  /** Queues a CSV of this discount's codes; the imports and exports page follows the job. */
  exportCodes: (storeId: string, id: string): Promise<ExportJob> =>
    postAdminV1StoresStoreIdExports(
      { storeId },
      { kind: 'discount_codes_csv', filters: { discountId: id } },
    ),
  usages: (
    storeId: string,
    id: string,
    params: GetAdminV1StoresStoreIdDiscountsIdUsagesParams,
  ): Promise<GetAdminV1StoresStoreIdDiscountsIdUsages200> =>
    getAdminV1StoresStoreIdDiscountsIdUsages({ storeId, id }, params),
};
